import type { BaseItineraryBlockProps, ItineraryItemAction } from './BaseItineraryBlock.js';

import React, { useEffect, useRef } from 'react';

import { sentry } from '#lib/sentry.js';
import { useRefreshPlace } from '#lib/use-refresh-place.js';
import { useApp, useItineraryState, useTripsState } from '#states/hooks.js';
import { ImageGalleryWidget } from '#ui/chat/index.js';
import { SimpleMarkdown } from '#ui/chat/SimpleMarkdown.js';
import { ItineraryDatesDialog } from '#ui/itinerary-dates-dialog.js';

import { BaseItineraryBlock } from './BaseItineraryBlock.js';
import { ItemNotesDialog } from './ItemNotesDialog.js';
import { bookingDetailActions, viewBookingDetailsAction } from './itinerary-booking-actions.js';
import { ItineraryDetailSummary } from './ItineraryDetailSummary.js';
import { ItineraryNotesSection } from './ItineraryNotesSection.js';
import { PrintBookingDetails } from './PrintBookingDetails.js';

export function PlaceBlock({ event, items, size = 'default', className }: BaseItineraryBlockProps) {
  const tripsState = useTripsState();
  const itineraryState = useItineraryState();
  const { features } = useApp('features');
  const flows = (features as { flows: Record<string, (...args: any[]) => Promise<unknown>> }).flows;
  const [isDatesDialogOpen, setIsDatesDialogOpen] = React.useState(false);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = React.useState(false);
  const enrichTriggeredRef = useRef(false);
  const placeRefId = items[0]?.place?.id;
  const needsEnrichment = items[0]?.place?.needsEnrichment ?? false;

  useRefreshPlace(placeRefId);

  // Trigger AI enrichment for the place (fire-and-forget)
  useEffect(() => {
    if (!placeRefId || !needsEnrichment || enrichTriggeredRef.current) return;
    enrichTriggeredRef.current = true;
    const enrichPlace = flows.enrichPlace as ((input: { placeId: string }) => Promise<unknown>) | undefined;
    if (!enrichPlace) return;
    void enrichPlace({ placeId: placeRefId }).catch((error: unknown) => {
      enrichTriggeredRef.current = false;
      sentry?.captureException(error);
    });
  }, [placeRefId, needsEnrichment, flows]);

  if (!event.type.startsWith('place')) return null;

  const primaryItem = items[0]!;
  const time = event.formattedTime;
  const title = event.title;
  const images = primaryItem.isPlace && primaryItem.place?.allImages?.length ? primaryItem.place.allImages : undefined;

  const isDefault = size === 'default';

  const place = primaryItem.place;
  const description = place?.generated?.description;

  let summaryContent: React.ReactNode = null;
  let detailRows: React.ReactNode = null;

  if (isDefault) {
    summaryContent =
      images || description ? (
        <>
          {images && images.length > 0 && (
            <ImageGalleryWidget images={images} aspect="video" round="md" maxImages={4} autoplay={false} />
          )}
          {description && <p className="text-sm max-w-prose text-pretty line-clamp-3">{description}</p>}
        </>
      ) : null;

    detailRows = (
      <>
        <ItineraryDetailSummary
          onShowGuide={place?.id ? () => tripsState.showPlaceGuide(place.id) : undefined}
          location={place?.formattedAddress}
          directionsUrl={place?.googleMapsLinks?.directionsUri}
          website={place?.website}
          phone={place?.phoneNumber}
          hours={place?.openingStatus}
          onShowHours={place?.id ? () => tripsState.showPlaceGuide(place.id) : undefined}
          cost={event.formattedCost}
          costDetail={primaryItem.formattedCostDetail}
        />
        <ItineraryNotesSection
          notes={primaryItem.notes}
          advisorNote={primaryItem.booking?.advisorNote}
          onEditNote={() => setIsNotesDialogOpen(true)}
        />
      </>
    );
  }

  if (size === 'lg') {
    // Address, hours, website and phone as icon rows, like the expanded card (no header chips).
    detailRows = (
      <ItineraryDetailSummary
        location={place?.formattedAddress}
        directionsUrl={place?.googleMapsLinks?.directionsUri}
        website={place?.website}
        phone={place?.phoneNumber}
        hours={place?.openingStatus}
        onShowHours={place?.id ? () => tripsState.showPlaceGuide(place.id) : undefined}
        onShowGuide={place?.id ? () => tripsState.showPlaceGuide(place.id) : undefined}
      />
    );

    summaryContent = description ? (
      <div className="max-w-prose text-pretty">
        <SimpleMarkdown text={description} />
      </div>
    ) : (
      <div className="space-y-2 max-w-prose">
        <div className="h-6 w-full animate-pulse bg-muted rounded" />
        <div className="h-6 w-full animate-pulse bg-muted rounded" />
        <div className="h-6 w-3/4 animate-pulse bg-muted rounded" />
      </div>
    );
  }

  const actions: ItineraryItemAction[] = [];

  // Places can't be booked through Elara, so recording the booking yourself is the primary action
  // here — both adding and editing. The details then render inline in the content above.
  if (isDefault) {
    actions.push(
      ...viewBookingDetailsAction(primaryItem, (item) => itineraryState.openViewBookingDialog(item)),
      ...bookingDetailActions(primaryItem, itineraryState, { add: 'secondary' }),
    );
  }

  // On the expanded and full-page layouts the guide is the "Read the full guide" row; the button is
  // only needed on the compact one.
  if (place?.id && size === 'sm') {
    actions.push({
      type: 'secondary',
      label: 'Guide',
      icon: 'menu_book',
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        tripsState.showPlaceGuide(place.id);
      },
    });
  }

  // Scheduling a place is a primary task in the builder, so surface it as a button
  // on the expanded card; it stays in the overflow menu on the other layouts.
  actions.push({
    type: isDefault ? 'secondary' : 'tertiary',
    label: 'Change time',
    icon: 'schedule',
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsDatesDialogOpen(true);
    },
  });

  // On the expanded builder card, removal is an explicit button; elsewhere it
  // stays in the overflow menu via `canDeleteItem`.
  if (isDefault) {
    actions.push({
      type: 'secondary',
      label: 'Remove',
      icon: 'delete',
      destructive: true,
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        itineraryState.openDeleteDialog(primaryItem);
      },
    });
  }

  // The website is an icon row on the expanded and full-page layouts; keep it as an explicit action
  // only on the compact one.
  const websiteUrl = place?.website;
  if (websiteUrl && size === 'sm') {
    actions.push({
      type: 'secondary',
      label: 'View website',
      icon: 'link',
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        window.open(websiteUrl, '_blank');
      },
    });
  }

  // On the expanded and full-page layouts directions are the address row's link, so the dedicated
  // action is only needed on the compact one.
  const directionsUrl = place?.googleMapsLinks?.directionsUri;
  if (directionsUrl && size === 'sm') {
    actions.push({
      type: 'secondary',
      label: 'Get directions',
      icon: 'directions',
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        window.open(directionsUrl, '_blank');
      },
    });
  }

  const handleConfirmDates = async (dates: { startDateTime: string; endDateTime?: string }) => {
    await itineraryState.updateItemDates(primaryItem.id, {
      startDateTime: dates.startDateTime,
      endDateTime: dates.endDateTime,
    });
    setIsDatesDialogOpen(false);
  };

  return (
    <>
      <BaseItineraryBlock
        title={title}
        event={event}
        items={items}
        size={size}
        className={className}
        canDeleteItem={!isDefault}
        images={images}
        preHeaderContent={
          size === 'lg' &&
          time && (
            <>
              <div>·</div>
              <div className="text-xs uppercase tracking-wide">{time}</div>
            </>
          )
        }
        onTimeClick={isDefault ? () => setIsDatesDialogOpen(true) : undefined}
        onClick={() => place?.id && tripsState.showPlaceGuide(place.id)}
        panelContent={
          images &&
          images.length > 0 && (
            <ImageGalleryWidget images={images} aspect="fill" round="none" maxImages={1} autoplay={true} />
          )
        }
        actions={actions}
        summary={summaryContent}
      >
        {detailRows}
        {size === 'lg' && <PrintBookingDetails item={primaryItem} />}
      </BaseItineraryBlock>

      {isDatesDialogOpen && (
        <ItineraryDatesDialog
          open={isDatesDialogOpen}
          onOpenChange={setIsDatesDialogOpen}
          itemType="place"
          referenceId={primaryItem.referenceId ?? undefined}
          title={event.title}
          initialStartDateTime={primaryItem.startDateTime}
          initialEndDateTime={primaryItem.endDateTime ?? undefined}
          tripStartDate={tripsState.current?.startDate ?? undefined}
          tripEndDate={tripsState.current?.endDate ?? undefined}
          onConfirm={handleConfirmDates}
          confirmLabel="Save"
        />
      )}

      {isNotesDialogOpen && (
        <ItemNotesDialog
          open={isNotesDialogOpen}
          onOpenChange={setIsNotesDialogOpen}
          initialNotes={primaryItem.notes}
          onConfirm={async (notes) => {
            await itineraryState.updateItemNotes(primaryItem.id, notes);
          }}
        />
      )}
    </>
  );
}
